/**
 * Events Types Service
 * Service for managing event types (concert, conference, party, etc.)
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://apicabaza.creago.be/rest/cabaza';
const API_KEY = 'cabaza_api_key_2024_change_me';

const headers = {
  'Content-Type': 'application/json',
  'X-API-Key': API_KEY
};

/**
 * Get all event types
 * @param {Object} options - Filter options
 * @param {string} options.category - Filter by category
 * @param {string} options.keyword - Search keyword
 * @param {string} options.lang - Language (fr, en, nl, de)
 * @param {boolean} options.active - Filter by active status
 */
export const getEventsTypes = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.category) params.append('category', options.category);
  if (options.keyword) params.append('keyword', options.keyword);
  if (options.lang) params.append('lang', options.lang);
  if (options.active !== undefined) params.append('active', options.active);

  const url = `${API_BASE_URL}/events_types${params.toString() ? '?' + params.toString() : ''}`;
  const response = await fetch(url, { headers });
  return response.json();
};

/**
 * Get event types grouped by category
 * @param {string} lang - Language (fr, en, nl, de)
 */
export const getEventsTypesGrouped = async (lang = 'fr') => {
  const response = await fetch(`${API_BASE_URL}/events_types/grouped?lang=${lang}`, { headers });
  return response.json();
};

/**
 * Get all categories
 */
export const getEventsCategories = async () => {
  const response = await fetch(`${API_BASE_URL}/events_types/categories`, { headers });
  return response.json();
};

/**
 * Get a single event type by ID
 * @param {number} id - Type ID
 * @param {string} lang - Language
 */
export const getEventTypeById = async (id, lang = 'fr') => {
  const response = await fetch(`${API_BASE_URL}/events_types/${id}?lang=${lang}`, { headers });
  return response.json();
};

/**
 * Get a single event type by code
 * @param {string} code - Type code
 * @param {string} lang - Language
 */
export const getEventTypeByCode = async (code, lang = 'fr') => {
  const response = await fetch(`${API_BASE_URL}/events_types/code/${code}?lang=${lang}`, { headers });
  return response.json();
};

/**
 * Create a new event type
 * @param {Object} typeData - Type data
 */
export const createEventType = async (typeData) => {
  const response = await fetch(`${API_BASE_URL}/events_types`, {
    method: 'POST',
    headers,
    body: JSON.stringify(typeData)
  });
  return response.json();
};

/**
 * Update an event type
 * @param {number} id - Type ID
 * @param {Object} typeData - Type data to update
 */
export const updateEventType = async (id, typeData) => {
  const response = await fetch(`${API_BASE_URL}/events_types/${id}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(typeData)
  });
  return response.json();
};

/**
 * Delete an event type
 * @param {number} id - Type ID
 */
export const deleteEventType = async (id) => {
  const response = await fetch(`${API_BASE_URL}/events_types/${id}`, {
    method: 'DELETE',
    headers
  });
  return response.json();
};

/**
 * Category labels for display
 */
export const categoryLabels = {
  'Professionnel': { fr: 'Professionnel', en: 'Professional', icon: 'Briefcase' },
  'Social': { fr: 'Social', en: 'Social', icon: 'Users' },
  'Sport': { fr: 'Sport', en: 'Sports', icon: 'Trophy' },
  'Culture': { fr: 'Culture', en: 'Culture', icon: 'Theater' },
  'Gastronomie': { fr: 'Gastronomie', en: 'Food & Drink', icon: 'UtensilsCrossed' },
  'Loisirs': { fr: 'Loisirs', en: 'Leisure', icon: 'Gamepad2' },
  'Bien-être': { fr: 'Bien-être', en: 'Wellness', icon: 'Heart' },
  'Cours': { fr: 'Cours & Ateliers', en: 'Classes & Workshops', icon: 'GraduationCap' },
  'Communautaire': { fr: 'Communautaire', en: 'Community', icon: 'Users' },
  'Enfants': { fr: 'Enfants', en: 'Kids', icon: 'Baby' },
  'Voyage': { fr: 'Voyage & Excursions', en: 'Travel & Trips', icon: 'Plane' },
  'Religieux': { fr: 'Religieux', en: 'Religious', icon: 'Church' },
  'Autre': { fr: 'Autre', en: 'Other', icon: 'MoreHorizontal' }
};

/**
 * Get translated category name
 * @param {string} category - Category key
 * @param {string} lang - Language code
 * @returns {string} Translated category name
 */
export const getCategoryTranslation = (category, lang = 'fr') => {
  return categoryLabels[category]?.[lang] || category;
};

/**
 * Get category info (translation, icon)
 * @param {string} category - Category key
 * @param {string} lang - Language code
 * @returns {Object} Category info { name, icon }
 */
export const getCategoryInfo = (category, lang = 'fr') => {
  const data = categoryLabels[category];
  if (!data) return { name: category, icon: 'MoreHorizontal' };
  return {
    name: data[lang] || category,
    icon: data.icon
  };
};

// Backward compatibility exports
export const getEventTypes = getEventsTypes;
export const getEventTypesGrouped = getEventsTypesGrouped;
export const getCategories = getEventsCategories;
export const CATEGORY_DATA = categoryLabels;
export const CATEGORY_TRANSLATIONS = categoryLabels;

export default {
  getEventsTypes,
  getEventsTypesGrouped,
  getEventsCategories,
  getEventTypeById,
  getEventTypeByCode,
  createEventType,
  updateEventType,
  deleteEventType,
  categoryLabels,
  getCategoryTranslation,
  getCategoryInfo
};
